/*
 * Copyright (c) Eric Traut
 * Wrapper interface around the monaco editor component. This class
 * handles language server interactions, the display of errors, etc.
 */

import { useDroppable } from '@dnd-kit/core';
import loader from '@monaco-editor/loader';
import Editor, { Monaco } from '@monaco-editor/react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { t } from 'i18next';
import * as monaco from 'monaco-editor';
import { ForwardedRef, MutableRefObject, forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  CompletionItem,
  CompletionItemKind,
  Diagnostic,
  DiagnosticSeverity,
  InsertReplaceEdit,
  MarkupContent,
  Range,
  TextDocumentEdit,
} from 'vscode-languageserver-types';
import { projectSlice, selectPendingProgramReplacementForLoadedProject } from '../../../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { useSettings } from '../../../settings/settings';
import {
  BPS_BLACK,
  BPS_BLUE,
  BPS_DARK_GREY,
  BPS_LIGHT_GREY,
  EDITOR_BACKGROUND_DARK,
  EDITOR_BACKGROUND_LIGHT,
  EDITOR_LINE_HIGHLIGHT_DARK,
  EDITOR_LINE_HIGHLIGHT_LIGHT,
  EDITOR_LINE_NUMBER_ACTIVE_DARK,
  EDITOR_LINE_NUMBER_ACTIVE_LIGHT,
  EDITOR_LINE_NUMBER_PASSIVE_DARK,
  EDITOR_LINE_NUMBER_PASSIVE_LIGHT,
  TEXT_PRIMARY_DARK,
  TEXT_PRIMARY_LIGHT,
  WHITE,
} from '../../../theme/theme-colors';
import { LAYOUT_CONSTANTS } from '../../../utils/layout-constants';
import {
  consumeRobotStartRemovalAllowance,
  findManagedRobotStartCallRanges,
  hasManagedRobotStartCall,
  hasRobotBaseImport,
  restoreManagedRobotStartCallInCode,
} from '../../../utils/monacoRegionUtils';
import { MAIN_PROGRAM_FILE_NAME } from '../../CodeEditor/utils/mainProgramEditor';
import type { BpsMainProgramEditor, MainProgramEditOptions } from '../../CodeEditor/utils/mainProgramEditor';
import { dropInfo } from '../../CodeEditor/types';
import { useActiveEditorInfoState } from '../active-editor-hooks';
import { DefinitionInfo, LSP_DOCUMENT_URI, LspClient } from './LspClient';
import './monaco.css';
import { registerPythonImportCodeActionProvider, registerPythonImportEditorActions } from './pythonImports/monaco';

loader.config({ monaco });

loader
  .init()
  .then((monaco) => {
    monaco.languages.registerHoverProvider('python', {
      provideHover: handleHoverRequest,
    });
    monaco.languages.registerSignatureHelpProvider('python', {
      provideSignatureHelp: handleSignatureHelpRequest,
      signatureHelpTriggerCharacters: ['(', ','],
    });
    monaco.languages.registerCompletionItemProvider('python', {
      provideCompletionItems: handleProvideCompletionRequest,
      resolveCompletionItem: handleResolveCompletionRequest,
      triggerCharacters: ['.', '[', '"', "'"],
    });
    monaco.languages.registerRenameProvider('python', {
      provideRenameEdits: handleRenameRequest,
    });
    registerPythonImportCodeActionProvider(monaco);
  })
  .catch((error) => console.error('An error occurred during initialization of Monaco: ', error));

const options: monaco.editor.IStandaloneEditorConstructionOptions = {
  selectOnLineNumbers: true,
  minimap: { enabled: false },
  fixedOverflowWidgets: true,
  tabCompletion: 'on',
  automaticLayout: true,
  hover: { enabled: true },
  scrollBeyondLastLine: false,
  autoClosingOvertype: 'always',
  autoSurround: 'quotes',
  autoIndent: 'full',
  // The default settings prefer "Menlo", but "Monaco" looks better
  // for our purposes. Swap the order so Monaco is used if available.
  fontFamily: 'Monaco, Menlo, "Courier New", monospace',
  fontSize: 16,
  showUnused: true,
  wordBasedSuggestions: false,
  overviewRulerLanes: 0,
  renderWhitespace: 'none',
  guides: {
    indentation: false,
  },
  renderLineHighlight: 'all',
};

const MANAGED_ROBOT_START_HOVER_DEFAULT = 'Required while Use RobotBase is on. Turn it off in the API panel to remove this function.';
const RENAME_INPUT_WIDGET_SELECTOR = '[widgetid="__renameInputWidget"]';
const VISIBLE_CONTENT_WIDGET_ATTRIBUTE = 'monaco-visible-content-widget';

// Deliberately no `overflowWidgetsDomNode`: Monaco must keep its overflow widget container inside the editor's own
// DOM node. Widgets that take DOM focus (the rename input) are cancelled by Monaco when focus leaves that node, and
// they never render visible when the container is hosted elsewhere. `fixedOverflowWidgets` plus the editor popup
// z-index is what keeps popups above surrounding app chrome.
function getEditorOptions(fontSize: number, readOnly: boolean): monaco.editor.IStandaloneEditorConstructionOptions {
  return {
    ...options,
    fontSize,
    readOnly,
  };
}

/**
 * Narrows a whole-program replacement to the span that actually differs, by trimming the common
 * prefix and suffix. Returns `null` when the texts are identical.
 *
 * Editing only the changed span lets Monaco's own marker tracking carry the caret, selection, and
 * decorations through the edit, and keeps the undo entry proportional to the real change. Replacing
 * the full model range instead would relocate the caret to text the user was not editing.
 */
function buildMinimalReplacementEdit(model: monaco.editor.ITextModel, replacementCode: string): monaco.editor.IIdentifiedSingleEditOperation | null {
  const current = model.getValue();
  if (current === replacementCode) return null;

  const maxCommon = Math.min(current.length, replacementCode.length);
  let prefix = 0;
  while (prefix < maxCommon && current[prefix] === replacementCode[prefix]) prefix++;

  let suffix = 0;
  while (suffix < maxCommon - prefix && current[current.length - 1 - suffix] === replacementCode[replacementCode.length - 1 - suffix]) suffix++;

  const start = model.getPositionAt(prefix);
  const end = model.getPositionAt(current.length - suffix);

  return {
    range: new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column),
    text: replacementCode.slice(prefix, replacementCode.length - suffix),
  };
}

/**
 * Applies a queued whole-program replacement without discarding editor state.
 *
 * `setValue` resets the model, which clears the undo stack and drops the caret to 1:1, so it is
 * reserved for read-only projects where `executeEdits` is rejected and there is no undo history to
 * protect. Identical content is skipped outright.
 */
function applyProgramReplacement(
  editor: monaco.editor.IStandaloneCodeEditor,
  model: monaco.editor.ITextModel,
  replacementCode: string,
  readOnly: boolean
): void {
  if (model.getValue() === replacementCode) return;

  if (readOnly) {
    editor.setValue(replacementCode);
    return;
  }

  const edit = buildMinimalReplacementEdit(model, replacementCode);
  if (!edit) return;

  editor.pushUndoStop();
  editor.executeEdits('program.replacement', [edit]);
  editor.pushUndoStop();
}

interface RegisteredModel {
  model: monaco.editor.ITextModel;
  lspClient: LspClient;
}
const registeredModels: RegisteredModel[] = [];
type ViewClassDefinitionHandler = (model: monaco.editor.ITextModel | null, position: monaco.Position) => Promise<void>;
type FormatPythonProgramHandler = () => Promise<void> | void;
export interface MonacoEditorProps {
  lspClient: LspClient;
  code: string;
  diagnostics: Diagnostic[];
  dropInfo: dropInfo;
  previewModelRef?: MutableRefObject<monaco.editor.ITextModel | null>;
  readOnly: boolean;
  onEditorMounted: (editor: monaco.editor.IStandaloneCodeEditor) => void;
  onUpdateCode: (code: string) => void;
  formatPythonActionLabel?: string;
  onFormatPythonProgram?: FormatPythonProgramHandler;
}

export interface MonacoEditorRef {
  focus: () => void;
  selectRange: (range: Range) => void;
}

monaco.editor.defineTheme('spheroLight', {
  base: 'vs',
  inherit: true,
  rules: [],
  colors: {
    'editor.background': EDITOR_BACKGROUND_LIGHT,
    'editorLineNumber.foreground': EDITOR_LINE_NUMBER_PASSIVE_LIGHT,
    'editorLineNumber.activeForeground': EDITOR_LINE_NUMBER_ACTIVE_LIGHT,
    'editor.lineHighlightBorder': EDITOR_LINE_HIGHLIGHT_LIGHT,
  },
});

monaco.editor.defineTheme('spheroDark', {
  base: 'vs-dark',
  inherit: true,
  rules: [],
  colors: {
    'editor.background': EDITOR_BACKGROUND_DARK,
    'editorLineNumber.foreground': EDITOR_LINE_NUMBER_PASSIVE_DARK,
    'editorLineNumber.activeForeground': EDITOR_LINE_NUMBER_ACTIVE_DARK,
    'editor.lineHighlightBorder': EDITOR_LINE_HIGHLIGHT_DARK,
  },
});

// Tab component for editor file tabs
interface EditorTabProps {
  fileName: string;
  isActive: boolean;
  onSelect: () => void;
  onClose: () => void;
}

const EditorTab = memo<EditorTabProps>(({ fileName, isActive, onSelect, onClose }) => {
  return (
    <Box
      onClick={onSelect}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 2.5,
        py: 1,
        borderRadius: '8px 8px 0 0',
        cursor: 'pointer',
        bgcolor: isActive ? theme.vars?.palette?.background?.editorTab : 'transparent',
        ...theme.applyStyles('light', {
          color: BPS_BLUE,
        }),
        ...theme.applyStyles('dark', {
          color: WHITE,
        }),
        border: isActive ? 1 : 'none',
        borderBottom: 'none',
        borderColor: isActive ? theme.vars?.palette?.divider : 'transparent',
        boxShadow: isActive ? (theme.palette.mode === 'dark' ? '0 -2px 4px rgba(0,0,0,0.2)' : '0 -2px 4px rgba(0,0,0,0.05)') : 'none',
        position: 'relative',
        zIndex: isActive ? 1 : 0,
        transition: 'background-color 0.2s, box-shadow 0.2s',
        '&:hover': {
          bgcolor: isActive ? theme.vars?.palette?.background?.editorTab : theme.vars?.palette?.action?.hover,
        },
      })}
    >
      <Typography variant="body2" fontSize={13.5} fontWeight={500}>
        {fileName}
      </Typography>
      {fileName !== MAIN_PROGRAM_FILE_NAME && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={(theme) => ({
            p: 0.375,
            ...theme.applyStyles('light', {
              color: BPS_BLUE,
            }),
            ...theme.applyStyles('dark', {
              color: WHITE,
            }),
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          })}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  );
});

EditorTab.displayName = 'EditorTab';

export const MonacoEditor = forwardRef(function MonacoEditor(props: MonacoEditorProps, ref: ForwardedRef<MonacoEditorRef>) {
  const { onUpdateCode } = props;
  const editorPopupZIndex = String(LAYOUT_CONSTANTS.Z_INDEX.EDITOR_POPUP);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const { mode } = useColorScheme();
  const [darkMode, setDarkMode] = useState(false);
  const project = useAppSelector((state) => state.project.loadedProject);
  const dispatch = useAppDispatch();

  const [, setEditorInfo] = useActiveEditorInfoState();

  const monacoRef = useRef<Monaco | null>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const overflowWidgetsObserverRef = useRef<MutationObserver | null>(null);
  const mainFileModelRef = useRef<monaco.editor.ITextModel | null>(null);
  const managedRobotStartDecorationsRef = useRef<monaco.editor.IEditorDecorationsCollection | null>(null);
  const managedRobotStartTooltipRef = useRef<HTMLDivElement | null>(null);
  const managedRobotStartHoverCleanupRef = useRef<(() => void) | null>(null);
  const managedRobotStartHoverMessageRef = useRef(MANAGED_ROBOT_START_HOVER_DEFAULT);
  const pendingProgramReplacement = useAppSelector(selectPendingProgramReplacementForLoadedProject);
  const [openClassFiles, setOpenClassFiles] = useState<{ [fileName: string]: { model: monaco.editor.ITextModel } }>({});
  const [activeFile, setActiveFile] = useState<string>(MAIN_PROGRAM_FILE_NAME);
  const [startRemovalDialogOpen, setStartRemovalDialogOpen] = useState(false);
  const [settings] = useSettings();
  const [editorOptions, setEditorOptions] = useState<monaco.editor.IStandaloneEditorConstructionOptions>(() =>
    getEditorOptions(settings.fontSize, props.readOnly)
  );

  const activeFileRef = useRef(activeFile);
  const lastAcceptedMainCodeRef = useRef(props.code);
  const lastManagedRobotRestoreRef = useRef<{ beforeCode: string; restoredCode: string } | null>(null);
  const lastEditorKeyRef = useRef<{ key: string; selection: monaco.IRange | null } | null>(null);
  const openClassFilesRef = useRef(openClassFiles);
  const mainFileModel = openClassFiles[MAIN_PROGRAM_FILE_NAME]?.model;
  const handleViewClassDefinitionRef = useRef<ViewClassDefinitionHandler>(async () => undefined);
  const formatPythonProgramRef = useRef<FormatPythonProgramHandler | undefined>(props.onFormatPythonProgram);
  const managedRobotStartHoverMessage =
    t('monacoEditor.commandManagement.managedStart.lockHover', {
      defaultValue: MANAGED_ROBOT_START_HOVER_DEFAULT,
    }) || MANAGED_ROBOT_START_HOVER_DEFAULT;
  managedRobotStartHoverMessageRef.current = managedRobotStartHoverMessage;

  const selectActiveFile = useCallback((fileName: string) => {
    activeFileRef.current = fileName;
    setActiveFile(fileName);
  }, []);

  const syncLoadedProjectCode = useCallback(
    (code: string, lastModified = new Date().toISOString()) => {
      dispatch(projectSlice.actions.onUpdateLastModified(lastModified));
      dispatch(projectSlice.actions.onUpdateProgram(code));
    },
    [dispatch]
  );

  useEffect(() => {
    openClassFilesRef.current = openClassFiles;
  }, [openClassFiles]);

  useEffect(() => {
    formatPythonProgramRef.current = props.onFormatPythonProgram;
  }, [props.onFormatPythonProgram]);

  useEffect(() => {
    lastAcceptedMainCodeRef.current = props.code;
  }, [project?.id, props.code]);

  useEffect(() => {
    setEditorInfo({
      canUndo: false,
      canRedo: false,
    });
  }, [setEditorInfo]);

  useEffect(() => {
    setEditorOptions(getEditorOptions(settings.fontSize, props.readOnly));
  }, [props.readOnly, settings.fontSize]);

  useEffect(() => {
    const root = document.documentElement;
    const properties = {
      '--bps-editor-popup-z-index': editorPopupZIndex,
      '--bps-editor-popup-background': darkMode ? EDITOR_BACKGROUND_DARK : WHITE,
      '--bps-editor-popup-foreground': darkMode ? TEXT_PRIMARY_DARK : TEXT_PRIMARY_LIGHT,
      '--bps-editor-popup-border': darkMode ? BPS_DARK_GREY : BPS_LIGHT_GREY,
      '--bps-editor-popup-code-background': darkMode ? BPS_BLACK : EDITOR_BACKGROUND_LIGHT,
      '--bps-editor-popup-status-background': darkMode ? BPS_BLACK : EDITOR_BACKGROUND_LIGHT,
    };
    const previousValues = Object.fromEntries(
      Object.keys(properties).map((propertyName) => [propertyName, root.style.getPropertyValue(propertyName)])
    );

    Object.entries(properties).forEach(([propertyName, value]) => {
      root.style.setProperty(propertyName, value);
    });

    return () => {
      Object.entries(properties).forEach(([propertyName, value]) => {
        const previousValue = previousValues[propertyName];
        if (previousValue) {
          root.style.setProperty(propertyName, previousValue);
        } else if (root.style.getPropertyValue(propertyName) === value) {
          root.style.removeProperty(propertyName);
        }
      });
    };
  }, [darkMode, editorPopupZIndex]);

  useEffect(() => {
    setDarkMode(mode === 'dark' ? true : mode === 'system' && prefersDarkMode ? true : false);
  }, [mode, prefersDarkMode]);

  // Rely on Monaco's automaticLayout; no manual window resize listener needed

  const applyOverflowWidgetsZIndex = useCallback(
    (overflowWidgetsDomNode: HTMLElement) => {
      const applyZIndex = (element: HTMLElement) => {
        if (element.style.getPropertyValue('z-index') !== editorPopupZIndex || element.style.getPropertyPriority('z-index') !== 'important') {
          element.style.setProperty('z-index', editorPopupZIndex, 'important');
        }
      };

      applyZIndex(overflowWidgetsDomNode);
      overflowWidgetsDomNode.querySelectorAll<HTMLElement>('[monaco-visible-content-widget], [widgetid]').forEach(applyZIndex);
    },
    [editorPopupZIndex]
  );

  const findOverflowWidgetsDomNode = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor) => {
      const overflowWidgetsDomNode = editor.getDomNode()?.querySelector<HTMLElement>('.overflowingContentWidgets') ?? null;
      if (overflowWidgetsDomNode) {
        applyOverflowWidgetsZIndex(overflowWidgetsDomNode);
      }
      return overflowWidgetsDomNode;
    },
    [applyOverflowWidgetsZIndex]
  );

  // Workaround for Monaco 0.44, tracked for removal by BPS-875. `RenameInputField._show()` requests a widget layout
  // and then focuses its input on a fixed 100ms timer. When the widget's first visible render lands after that timer,
  // `focus()` runs against a still-hidden element and is silently dropped, so the rename box is left open but unable
  // to accept keystrokes (BPS-871). Focus the input ourselves once the widget actually becomes visible.
  const focusVisibleRenameInput = useCallback((overflowWidgetsDomNode: HTMLElement) => {
    const renameWidget = overflowWidgetsDomNode.querySelector<HTMLElement>(RENAME_INPUT_WIDGET_SELECTOR);
    if (renameWidget?.getAttribute(VISIBLE_CONTENT_WIDGET_ATTRIBUTE) !== 'true') {
      return;
    }

    const renameInput = renameWidget.querySelector('input');
    if (!renameInput || renameInput.ownerDocument.activeElement === renameInput) {
      return;
    }

    renameInput.focus();

    // Monaco stores the selection it wants on the input itself; re-apply it so recovered focus still selects the
    // existing symbol name rather than leaving the caret wherever focus() put it.
    const selectionStart = Number.parseInt(renameInput.getAttribute('selectionStart') ?? '', 10);
    const selectionEnd = Number.parseInt(renameInput.getAttribute('selectionEnd') ?? '', 10);
    if (Number.isFinite(selectionStart) && Number.isFinite(selectionEnd)) {
      renameInput.setSelectionRange(selectionStart, selectionEnd);
    }
  }, []);

  const observeOverflowWidgetsDomNode = useCallback(
    (overflowWidgetsDomNode: HTMLElement) => {
      if (typeof globalThis.MutationObserver === 'undefined') {
        return;
      }
      overflowWidgetsObserverRef.current?.disconnect();
      const observer = new globalThis.MutationObserver(() => {
        applyOverflowWidgetsZIndex(overflowWidgetsDomNode);
        focusVisibleRenameInput(overflowWidgetsDomNode);
      });
      observer.observe(overflowWidgetsDomNode, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', VISIBLE_CONTENT_WIDGET_ATTRIBUTE],
      });
      overflowWidgetsObserverRef.current = observer;
    },
    [applyOverflowWidgetsZIndex, focusVisibleRenameInput]
  );

  const setupOverflowWidgetsDomNode = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor) => {
      const overflowWidgetsDomNode = findOverflowWidgetsDomNode(editor);
      if (overflowWidgetsDomNode) {
        observeOverflowWidgetsDomNode(overflowWidgetsDomNode);
      }
    },
    [findOverflowWidgetsDomNode, observeOverflowWidgetsDomNode]
  );

  const hideManagedRobotStartTooltip = useCallback(() => {
    managedRobotStartTooltipRef.current?.remove();
    managedRobotStartTooltipRef.current = null;
  }, []);

  const showManagedRobotStartTooltip = useCallback((event: MouseEvent, message: string) => {
    if (typeof document === 'undefined' || !message) {
      return;
    }

    let tooltip = managedRobotStartTooltipRef.current;
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = 'managed-robotbase-lock-tooltip';
      tooltip.textContent = message;
      document.body.appendChild(tooltip);
      managedRobotStartTooltipRef.current = tooltip;
    } else if (tooltip.textContent !== message) {
      tooltip.textContent = message;
    }

    tooltip.style.left = `${event.clientX + 12}px`;
    tooltip.style.top = `${event.clientY + 12}px`;
  }, []);

  const setupManagedRobotStartTooltip = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor) => {
      const editorDomNode = editor.getDomNode();
      if (!editorDomNode) {
        return;
      }

      const handleMouseMove = (event: MouseEvent) => {
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('.managed-robotbase-locked-gutter')) {
          showManagedRobotStartTooltip(event, managedRobotStartHoverMessageRef.current);
          return;
        }

        hideManagedRobotStartTooltip();
      };

      managedRobotStartHoverCleanupRef.current?.();
      editorDomNode.addEventListener('mousemove', handleMouseMove);
      editorDomNode.addEventListener('mouseleave', hideManagedRobotStartTooltip);
      managedRobotStartHoverCleanupRef.current = () => {
        editorDomNode.removeEventListener('mousemove', handleMouseMove);
        editorDomNode.removeEventListener('mouseleave', hideManagedRobotStartTooltip);
      };
    },
    [hideManagedRobotStartTooltip, showManagedRobotStartTooltip]
  );

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }
    setupOverflowWidgetsDomNode(editorRef.current);
  }, [setupOverflowWidgetsDomNode]);

  useEffect(() => {
    return () => {
      overflowWidgetsObserverRef.current?.disconnect();
      hideManagedRobotStartTooltip();
      managedRobotStartHoverCleanupRef.current?.();
      managedRobotStartHoverCleanupRef.current = null;
    };
  }, [hideManagedRobotStartTooltip]);

  function handleEditorDidMount(editor: monaco.editor.IStandaloneCodeEditor, monaco: Monaco) {
    editorRef.current = editor;
    props.onEditorMounted(editor);
    monacoRef.current = monaco;
    setupOverflowWidgetsDomNode(editor);
    setupManagedRobotStartTooltip(editor);
    const importActionDisposables = registerPythonImportEditorActions(editor, monaco, props.lspClient) ?? [];
    const classDefinitionActionDisposable = editor.addAction({
      id: 'go-to-class-definition',
      label: t('monacoEditor.go-to-class-definition', 'Go to Class Definition'),
      contextMenuGroupId: 'navigation',
      run: (ed) => {
        const position = ed.getPosition();
        if (!position) return;
        return handleViewClassDefinitionRef.current(ed.getModel(), position);
      },
    });
    const formatPythonActionDisposable = props.onFormatPythonProgram
      ? editor.addAction({
          id: 'python.formatProgram',
          label: props.formatPythonActionLabel ?? t('bottomControlBar.formatPython', 'Format Python code'),
          contextMenuGroupId: '1_modification',
          contextMenuOrder: 1.7,
          precondition: '!editorReadonly',
          run: () => formatPythonProgramRef.current?.(),
        })
      : undefined;

    editor.focus();
    updateUndoRedoDepth(editor);

    editor.updateOptions({ readOnly: props.readOnly });
    const model = editor.getModel();
    if (model) {
      mainFileModelRef.current = model;
      const mainFile = {
        [MAIN_PROGRAM_FILE_NAME]: { model },
      };
      openClassFilesRef.current = mainFile;
      setOpenClassFiles(mainFile);
    }

    // Listen for content changes to update undo/redo depth
    const modelContentDisposable = editor.onDidChangeModelContent(() => updateUndoRedoDepth(editor));
    let editorDisposed = false;
    // Monaco 0.44 can retain its transient disabled hover state when an open context menu is reopened.
    // Restore BPS's configured hover behavior after Monaco's synchronous context-menu handlers finish.
    const contextMenuHoverDisposable = editor.onContextMenu(() => {
      queueMicrotask(() => {
        if (!editorDisposed) {
          editor.updateOptions({ hover: { enabled: true } });
        }
      });
    });
    const keyDownDisposable = editor.onKeyDown((event) => {
      const key = event.browserEvent.key;
      lastEditorKeyRef.current = key === 'Backspace' || key === 'Delete' ? { key, selection: editor.getSelection() } : null;
    });
    editor.onDidDispose(() => {
      editorDisposed = true;
      managedRobotStartDecorationsRef.current?.clear();
      managedRobotStartDecorationsRef.current = null;
      hideManagedRobotStartTooltip();
      managedRobotStartHoverCleanupRef.current?.();
      managedRobotStartHoverCleanupRef.current = null;
      modelContentDisposable?.dispose();
      contextMenuHoverDisposable.dispose();
      keyDownDisposable?.dispose();
      importActionDisposables.forEach((disposable) => disposable.dispose());
      classDefinitionActionDisposable.dispose();
      formatPythonActionDisposable?.dispose();
      Object.entries(openClassFilesRef.current).forEach(([fileName, file]) => {
        if (fileName !== MAIN_PROGRAM_FILE_NAME && !file.model.isDisposed()) {
          file.model.dispose();
        }
      });
    });
  }

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.updateOptions({ readOnly: props.readOnly });
  }, [props.readOnly]);

  const openOrRevealFile = useCallback(
    async (uri: string, sourceCode: string) => {
      const fileName = uri.split('/').pop()!;

      // Check if file is already open
      const existingFile = openClassFilesRef.current[fileName];
      if (existingFile) {
        // Just switch to the tab
        selectActiveFile(fileName);
        editorRef.current?.setModel(existingFile.model);
        editorRef.current?.updateOptions({
          readOnly: fileName !== MAIN_PROGRAM_FILE_NAME || props.readOnly,
        });
        return;
      }

      const modelUri = monaco.Uri.parse(uri);
      const existingModel = monaco.editor.getModel(modelUri);
      const model = existingModel ?? monaco.editor.createModel(sourceCode, 'python', modelUri);

      if (!existingModel) {
        monaco.editor.setModelLanguage(model, 'python');
      }

      // Update open class files and switch to tab
      setOpenClassFiles((prev) => {
        const nextFiles = {
          ...prev,
          [fileName]: { model },
        };
        openClassFilesRef.current = nextFiles;
        return nextFiles;
      });
      selectActiveFile(fileName);
    },
    [props.readOnly, selectActiveFile]
  );

  const handleViewClassDefinition = useCallback(
    async (model: monaco.editor.ITextModel | null, position: monaco.Position) => {
      const lspClient = props.lspClient;

      if (!model) {
        if (!editorRef.current) return;
        showOverlayMessageAboveCursor(editorRef.current, t('monacoEditor.editorMessage.no-class-definition-found', 'No class definition found'));
        return;
      }

      const lspPosition = {
        line: position.lineNumber - 1,
        character: position.column - 1,
      };
      const documentUri = model === mainFileModelRef.current ? LSP_DOCUMENT_URI : model.uri.toString();

      const spheroTypeDefinitionLocation = (await lspClient.getGotoDefinitionInfo(model.getValue(), lspPosition, documentUri)) as DefinitionInfo[];

      if (!spheroTypeDefinitionLocation) {
        if (!editorRef.current) return;
        showOverlayMessageAboveCursor(editorRef.current, t('monacoEditor.editorMessage.no-class-definition-found', 'No class definition found'));
        return;
      }
      openOrRevealFile(spheroTypeDefinitionLocation[0].uri, spheroTypeDefinitionLocation[0].sourceCode);
    },
    [props.lspClient, openOrRevealFile]
  );
  handleViewClassDefinitionRef.current = handleViewClassDefinition;

  // Function to update undo/redo depth
  const updateUndoRedoDepth = (editor: monaco.editor.IStandaloneCodeEditor) => {
    const model = editor.getModel();
    if (model) {
      // The canUndo and canRedo methods in the Monaco editor model do seem work.
      // They are marked as internal, which messes with TypeScript.  But I could
      // not find a better way to see if undo/redo could be performed.
      // These control the visibility of the undo/redo control buttons only.
      setEditorInfo({
        canUndo: (model as unknown as { canUndo(): boolean }).canUndo(),
        canRedo: (model as unknown as { canRedo(): boolean }).canRedo(),
      });
    }
  };

  const showMainProgram = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || !mainFileModel) return false;

    selectActiveFile(MAIN_PROGRAM_FILE_NAME);
    editor.setModel(mainFileModel);
    editor.updateOptions({ readOnly: props.readOnly });
    return true;
  }, [mainFileModel, props.readOnly, selectActiveFile]);

  const applyMainProgramEdits = useCallback(
    (source: string, edits: monaco.editor.IIdentifiedSingleEditOperation[], options: MainProgramEditOptions = {}) => {
      const editor = editorRef.current;
      if (!editor || !mainFileModel || edits.length === 0) return false;

      if (!showMainProgram()) return false;

      const shouldPushUndoStop = options.undoStop !== false;
      if (shouldPushUndoStop) {
        editor.pushUndoStop();
      }
      editor.executeEdits(source, edits);
      if (shouldPushUndoStop) {
        editor.pushUndoStop();
      }

      const nextCode = mainFileModel.getValue();
      lastAcceptedMainCodeRef.current = nextCode;
      onUpdateCode(nextCode);
      setOpenClassFiles((prev) => {
        const nextFiles = {
          ...prev,
          [MAIN_PROGRAM_FILE_NAME]: { model: mainFileModel },
        };
        openClassFilesRef.current = nextFiles;
        return nextFiles;
      });

      if (options.focusEditor !== false) {
        editor.focus();
      }
      return true;
    },
    [mainFileModel, onUpdateCode, showMainProgram]
  );

  useEffect(() => {
    const editor = editorRef.current as BpsMainProgramEditor | null;
    if (!editor) return;

    editor.getMainProgramModel = () => mainFileModel;
    editor.getMainProgramCode = () => mainFileModel?.getValue() ?? props.code;
    editor.showMainProgram = showMainProgram;
    editor.applyMainProgramEdits = applyMainProgramEdits;
  }, [applyMainProgramEdits, mainFileModel, props.code, showMainProgram]);

  useImperativeHandle(ref, () => {
    return {
      focus: () => {
        const editor: monaco.editor.IStandaloneCodeEditor | null = editorRef.current;
        if (editor) {
          editor.focus();
        }
      },
      selectRange: (range: Range) => {
        const editor: monaco.editor.IStandaloneCodeEditor | null = editorRef.current;
        if (editor) {
          const monacoRange = convertRange(range);
          editor.setSelection(monacoRange);
          editor.revealLineInCenterIfOutsideViewport(monacoRange.startLineNumber);
        }
      },
    };
  });

  useEffect(() => {
    const monaco = monacoRef.current;
    if (monaco && mainFileModel) {
      setFileMarkers(monaco, mainFileModel, props.diagnostics);

      // Register the editor and the LSP Client so they can be accessed
      // by the hover provider, etc. Keep this scoped to the editable
      // program model; opened class tabs are read-only reference models.
      registerModel(mainFileModel, props.lspClient);
    }
  }, [mainFileModel, props.diagnostics, props.lspClient]);

  // Custom popup for displaying message in editor
  function showOverlayMessageAboveCursor(editor: monaco.editor.IStandaloneCodeEditor, message: string) {
    const widgetId = 'no-definition-overlay';

    // Remove existing widget if present
    const existingWidget = document.getElementById(widgetId);
    if (existingWidget) existingWidget.remove();

    const domNode = document.createElement('div');
    domNode.id = widgetId;
    domNode.style.position = 'absolute';
    domNode.style.background = '#252526';
    domNode.style.color = '#ffffff';
    domNode.style.padding = '6px 12px';
    domNode.style.borderRadius = '4px';
    domNode.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.5)';
    domNode.style.whiteSpace = 'nowrap';
    domNode.style.fontSize = '13px';
    domNode.style.pointerEvents = 'none';
    domNode.innerText = message;

    // Create arrow aligned to the left
    const arrow = document.createElement('div');
    arrow.style.position = 'absolute';
    arrow.style.top = '100%';
    arrow.style.left = '12px'; // match padding-left
    arrow.style.width = '0';
    arrow.style.height = '0';
    arrow.style.borderLeft = '6px solid transparent';
    arrow.style.borderRight = '6px solid transparent';
    arrow.style.borderTop = '6px solid #252526';
    domNode.appendChild(arrow);

    // Add it to editor container
    const container = editor.getDomNode();
    if (!container) return;

    container.appendChild(domNode);

    // Position above the cursor
    const position = editor.getPosition();
    if (position) {
      const cursorCoords = editor.getScrolledVisiblePosition(position);
      if (cursorCoords) {
        const top = cursorCoords.top - 40; // above cursor
        const left = cursorCoords.left - 15;

        domNode.style.top = `${top}px`;
        domNode.style.left = `${left}px`;
      }
    }

    // Remove after 2.5 seconds
    setTimeout(() => {
      domNode.remove();
    }, 2500);
  }

  const closeTab = (fileName: string) => {
    setOpenClassFiles((prev) => {
      const newFiles = { ...prev };

      newFiles[fileName].model.dispose();

      delete newFiles[fileName];
      openClassFilesRef.current = newFiles;

      if (activeFile === fileName) {
        const remainingFiles = Object.keys(newFiles);
        selectActiveFile(remainingFiles.length > 0 ? remainingFiles[0] : '');
        if (remainingFiles.length > 0) {
          editorRef.current?.setModel(newFiles[remainingFiles[0]].model);
          editorRef.current?.updateOptions({
            readOnly: remainingFiles[0] !== MAIN_PROGRAM_FILE_NAME || props.readOnly,
          });
        } else {
          editorRef.current?.setModel(null); // or handle showing a blank editor
        }
      }

      return newFiles;
    });
  };

  const { setNodeRef } = useDroppable({
    id: 'editor',
  });

  useEffect(() => {
    if (!pendingProgramReplacement) return;

    const editor = editorRef.current;
    if (!editor || !mainFileModel) return;
    const replacementCode = pendingProgramReplacement.code;
    lastAcceptedMainCodeRef.current = replacementCode;

    mainFileModelRef.current = mainFileModel;
    selectActiveFile(MAIN_PROGRAM_FILE_NAME);
    editor.setModel(mainFileModel);
    editor.updateOptions({ readOnly: props.readOnly });
    applyProgramReplacement(editor, mainFileModel, replacementCode, props.readOnly);

    try {
      props.lspClient.updateCode(replacementCode);
      props.lspClient.updateTextDocument(replacementCode).catch(() => {
        // Ignore errors
      });
    } catch {
      // Ignore LSP client update errors
    }

    syncLoadedProjectCode(replacementCode, pendingProgramReplacement.lastModified);
    dispatch(projectSlice.actions.clearProgramReplacement());
  }, [dispatch, mainFileModel, pendingProgramReplacement, props.lspClient, props.readOnly, selectActiveFile, syncLoadedProjectCode]);

  useEffect(() => {
    if (activeFileRef.current !== activeFile) return;

    const activeModel = openClassFiles[activeFile]?.model;
    if (!editorRef.current || !activeModel) return;

    editorRef.current.setModel(activeModel);
    // File is read-only if it's not Main.py OR if the project itself is read-only
    const readOnly = activeFile !== MAIN_PROGRAM_FILE_NAME || props.readOnly;
    editorRef.current.updateOptions({ readOnly: readOnly });
  }, [activeFile, openClassFiles, props.readOnly]);

  useEffect(() => {
    const editor = editorRef.current;
    const model = openClassFiles[activeFile]?.model;
    if (!editor) return;

    if (!managedRobotStartDecorationsRef.current) {
      managedRobotStartDecorationsRef.current = editor.createDecorationsCollection?.() ?? null;
    }
    if (!managedRobotStartDecorationsRef.current) {
      return;
    }

    if (!model || activeFile !== MAIN_PROGRAM_FILE_NAME) {
      managedRobotStartDecorationsRef.current.clear();
      return;
    }

    const decorations: monaco.editor.IModelDeltaDecoration[] = [];
    const commandManagementEnabled = project?.commandManagementEnabled ?? true;
    const [firstStartRange] = findManagedRobotStartCallRanges(model);
    if (commandManagementEnabled && firstStartRange) {
      decorations.push({
        range: new monaco.Range(firstStartRange.startLine, 1, firstStartRange.endLine, model.getLineMaxColumn(firstStartRange.endLine)),
        options: {
          isWholeLine: true,
          className: 'managed-robotbase-locked-line',
          blockClassName: 'managed-robotbase-locked-block',
          linesDecorationsClassName: 'managed-robotbase-locked-gutter',
        },
      });
    }

    if (decorations.length > 0) managedRobotStartDecorationsRef.current.set(decorations);
    else managedRobotStartDecorationsRef.current.clear();
  }, [activeFile, openClassFiles, project?.commandManagementEnabled]);

  const getPreRejectedEditSelection = useCallback(
    (event: monaco.editor.IModelContentChangedEvent | undefined, fallbackSelection: monaco.IRange | null) => {
      const changes = event?.changes;
      const change = changes?.length === 1 ? changes[0] : null;
      if (!change) {
        return fallbackSelection;
      }

      const lastEditorKey = lastEditorKeyRef.current;
      lastEditorKeyRef.current = null;
      const range = change.range;
      if (change.rangeLength > 0 && change.text.length === 0) {
        const selectionBeforeKey = lastEditorKey?.selection;
        const hadSelectionBeforeKey =
          selectionBeforeKey &&
          (selectionBeforeKey.startLineNumber !== selectionBeforeKey.endLineNumber ||
            selectionBeforeKey.startColumn !== selectionBeforeKey.endColumn);
        if (hadSelectionBeforeKey) {
          return selectionBeforeKey;
        }

        if (lastEditorKey?.key === 'Backspace') {
          return {
            startLineNumber: range.endLineNumber,
            startColumn: range.endColumn,
            endLineNumber: range.endLineNumber,
            endColumn: range.endColumn,
          };
        }

        return {
          startLineNumber: range.startLineNumber,
          startColumn: range.startColumn,
          endLineNumber: range.startLineNumber,
          endColumn: range.startColumn,
        };
      }

      if (change.rangeLength > 0) {
        return {
          startLineNumber: range.startLineNumber,
          startColumn: range.startColumn,
          endLineNumber: range.endLineNumber,
          endColumn: range.endColumn,
        };
      }

      return {
        startLineNumber: range.startLineNumber,
        startColumn: range.startColumn,
        endLineNumber: range.startLineNumber,
        endColumn: range.startColumn,
      };
    },
    []
  );

  const restoreSelectionAfterManagedRestore = useCallback((selection: monaco.IRange | null) => {
    if (selection && editorRef.current) {
      editorRef.current.setSelection(selection);
    }
  }, []);

  const startRemovalDialogTitle =
    t('monacoEditor.commandManagement.requiredStartDialog.title', { defaultValue: 'Use RobotBase Is Enabled' }) || 'Use RobotBase Is Enabled';
  const startRemovalDialogBody =
    t('monacoEditor.commandManagement.requiredStartDialog.body', {
      defaultValue:
        'The managed RobotBase import and RobotBase().start() call are required while Use RobotBase is enabled. Turn off Use RobotBase in the API panel before removing them.',
    }) ||
    'The managed RobotBase import and RobotBase().start() call are required while Use RobotBase is enabled. Turn off Use RobotBase in the API panel before removing them.';
  const startRemovalDialogConfirm = t('common.ok', { defaultValue: 'OK' }) || 'OK';

  return (
    <Stack
      height="100%"
      direction="column"
      flexGrow={1}
      minWidth={0}
      minHeight={0}
      pr={0}
      pt={0}
      position={'relative'}
      sx={{ '--bps-editor-popup-z-index': editorPopupZIndex }}
    >
      <Stack direction="row-reverse" flexGrow={1} minWidth={0} minHeight={0}>
        <Box
          flexGrow={1}
          id="editor"
          ref={setNodeRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
          }}
        >
          {/* Header: Tabs */}
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1,
              px: 0.5,
              py: 0.5,
              bgcolor: 'background.console',
              minWidth: 0,
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                height: 6,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.action.hover,
                borderRadius: 999,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.action.selected,
                borderRadius: 999,
                '&:hover': {
                  backgroundColor: theme.palette.action.active,
                },
              },
              ...theme.applyStyles('dark', {
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255, 255, 255, 0.24)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.32)',
                  },
                },
              }),
            })}
          >
            {Object.keys(openClassFiles).map((fileName) => (
              <EditorTab
                key={fileName}
                fileName={fileName}
                isActive={fileName === activeFile}
                onSelect={() => selectActiveFile(fileName)}
                onClose={() => closeTab(fileName)}
              />
            ))}
          </Box>

          {/* Content: Editor */}
          <Box sx={{ flexGrow: 1, minHeight: 0 }}>
            <Editor
              options={editorOptions}
              language="python"
              defaultValue={props.code}
              theme={darkMode ? 'spheroDark' : 'spheroLight'}
              onChange={(value: string | undefined, event?: monaco.editor.IModelContentChangedEvent) => {
                if (props.previewModelRef?.current && editorRef.current?.getModel() === props.previewModelRef.current) {
                  return;
                }
                const code = value ?? '';
                const commandManagementEnabled = project?.commandManagementEnabled ?? true;
                const isMainFile = activeFileRef.current === MAIN_PROGRAM_FILE_NAME;
                const previousCode = lastAcceptedMainCodeRef.current;
                const startCallRemoved = isMainFile && hasManagedRobotStartCall(previousCode) && !hasManagedRobotStartCall(code);
                const robotBaseImportMissing = isMainFile && hasManagedRobotStartCall(code) && !hasRobotBaseImport(code);
                const managedRequirementsRemoved = startCallRemoved || robotBaseImportMissing;
                const wholeProgramCleared = code.trim().length === 0;

                if (managedRequirementsRemoved) {
                  const model = editorRef.current?.getModel();
                  const blockedEditSelection = !wholeProgramCleared
                    ? getPreRejectedEditSelection(event, editorRef.current?.getSelection() ?? null)
                    : null;
                  const intentionallyAllowedRemoval = model ? consumeRobotStartRemovalAllowance(model) : false;
                  if (commandManagementEnabled && !intentionallyAllowedRemoval) {
                    const lastManagedRestore = lastManagedRobotRestoreRef.current;
                    if (event?.isUndoing && lastManagedRestore && previousCode === lastManagedRestore.restoredCode && startCallRemoved && model) {
                      editorRef.current?.executeEdits('robot.start.undo-managed-restore', [
                        {
                          range: new monaco.Range(1, 1, model.getLineCount(), model.getLineMaxColumn(model.getLineCount())),
                          text: lastManagedRestore.beforeCode,
                          forceMoveMarkers: true,
                        },
                      ]);
                      restoreSelectionAfterManagedRestore(blockedEditSelection);
                      lastManagedRobotRestoreRef.current = null;
                      return;
                    }

                    const restoredCode =
                      !wholeProgramCleared && startCallRemoved ? previousCode : restoreManagedRobotStartCallInCode(previousCode, code);
                    if (model && restoredCode !== null) {
                      editorRef.current?.executeEdits('robot.start.restore-managed', [
                        {
                          range: new monaco.Range(1, 1, model.getLineCount(), model.getLineMaxColumn(model.getLineCount())),
                          text: restoredCode,
                          forceMoveMarkers: true,
                        },
                      ]);
                      restoreSelectionAfterManagedRestore(blockedEditSelection);
                      lastManagedRobotRestoreRef.current = { beforeCode: previousCode, restoredCode };
                    }
                    if (!wholeProgramCleared) {
                      setStartRemovalDialogOpen(true);
                    }
                    lastEditorKeyRef.current = null;
                    return;
                  }
                }

                if (isMainFile) {
                  lastEditorKeyRef.current = null;
                  lastAcceptedMainCodeRef.current = code;
                  if (lastManagedRobotRestoreRef.current?.restoredCode !== code) {
                    lastManagedRobotRestoreRef.current = null;
                  }
                }
                props.onUpdateCode(code);
                const model = editorRef.current?.getModel();
                if (model) {
                  setOpenClassFiles((prev) => {
                    const nextFiles = {
                      ...prev,
                      [activeFileRef.current]: { model },
                    };
                    openClassFilesRef.current = nextFiles;
                    return nextFiles;
                  });
                }
              }}
              onMount={handleEditorDidMount}
            />
          </Box>
        </Box>
      </Stack>
      <Dialog open={startRemovalDialogOpen} onClose={() => setStartRemovalDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{startRemovalDialogTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{startRemovalDialogBody}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartRemovalDialogOpen(false)} variant="contained" autoFocus>
            {startRemovalDialogConfirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
});

function setFileMarkers(monaco: Monaco, model: monaco.editor.ITextModel, diagnostics: Diagnostic[]) {
  const markers: monaco.editor.IMarkerData[] = [];

  diagnostics.forEach((diag) => {
    const markerData: monaco.editor.IMarkerData = {
      ...convertRange(diag.range),
      severity: convertSeverity(diag.severity),
      message: diag.message,
      code: diag.code !== undefined ? String(diag.code) : undefined,
      source: diag.source ?? 'pyright',
    };

    if (diag.tags) {
      markerData.tags = diag.tags;
    }
    markers.push(markerData);
  });

  monaco.editor.setModelMarkers(model, 'pyright', markers);
}

function convertSeverity(severity: DiagnosticSeverity | undefined): monaco.MarkerSeverity {
  if (severity === undefined) {
    return monaco.MarkerSeverity.Error;
  }
  switch (severity) {
    case DiagnosticSeverity.Error:
    default:
      return monaco.MarkerSeverity.Error;

    case DiagnosticSeverity.Warning:
      return monaco.MarkerSeverity.Warning;

    case DiagnosticSeverity.Information:
      return monaco.MarkerSeverity.Info;

    case DiagnosticSeverity.Hint:
      return monaco.MarkerSeverity.Hint;
  }
}

function convertRange(range: Range): monaco.IRange {
  return {
    startLineNumber: range.start.line + 1,
    startColumn: range.start.character + 1,
    endLineNumber: range.end.line + 1,
    endColumn: range.end.character + 1,
  };
}

async function handleHoverRequest(model: monaco.editor.ITextModel, position: monaco.Position): Promise<monaco.languages.Hover | null> {
  const lspClient = getLspClientForModel(model);

  if (!lspClient) {
    return null;
  }

  try {
    const hoverInfo = await lspClient.getHoverInfo(model.getValue(), {
      line: position.lineNumber - 1,
      character: position.column - 1,
    });

    if (hoverInfo == null) {
      return null;
    }

    if (!hoverInfo.range) {
      return {
        contents: [
          {
            value: (hoverInfo.contents as MarkupContent).value,
          },
        ],
      };
    }
    return {
      contents: [
        {
          value: (hoverInfo.contents as MarkupContent).value,
        },
      ],
      range: convertRange(hoverInfo.range),
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return null;
  }
}

async function handleRenameRequest(
  model: monaco.editor.ITextModel,
  position: monaco.Position,
  newName: string
): Promise<monaco.languages.WorkspaceEdit | null> {
  const lspClient = getLspClientForModel(model);
  if (!lspClient) {
    return null;
  }

  try {
    const renameEdits = await lspClient.getRenameEdits(
      model.getValue(),
      {
        line: position.lineNumber - 1,
        character: position.column - 1,
      },
      newName
    );

    const edits: monaco.languages.IWorkspaceTextEdit[] = [];

    if (renameEdits?.documentChanges) {
      for (const docChange of renameEdits.documentChanges) {
        if (TextDocumentEdit.is(docChange)) {
          for (const textEdit of docChange.edits) {
            edits.push({
              resource: model.uri,
              versionId: undefined,
              textEdit: {
                range: convertRange(textEdit.range),
                text: textEdit.newText,
              },
            });
          }
        }
      }
    }

    return { edits };
  } catch {
    return null;
  }
}

async function handleSignatureHelpRequest(
  model: monaco.editor.ITextModel,
  position: monaco.Position
): Promise<monaco.languages.SignatureHelpResult | null> {
  const lspClient = getLspClientForModel(model);
  if (!lspClient) {
    return null;
  }

  try {
    const sigInfo = await lspClient.getSignatureHelp(model.getValue(), {
      line: position.lineNumber - 1,
      character: position.column - 1,
    });

    if (!sigInfo) {
      return null;
    }

    return {
      value: {
        signatures: sigInfo.signatures.map((sig) => {
          return {
            label: sig.label,
            documentation: sig.documentation,
            parameters: sig.parameters ?? [],
            activeParameter: sig.activeParameter ?? 0,
          };
        }),
        activeSignature: sigInfo.activeSignature ?? 0,
        activeParameter: sigInfo.activeParameter ?? 0,
      },
      dispose: () => {},
    };
  } catch {
    return null;
  }
}

async function handleProvideCompletionRequest(
  model: monaco.editor.ITextModel,
  position: monaco.Position
): Promise<monaco.languages.CompletionList | null> {
  const lspClient = getLspClientForModel(model);
  if (!lspClient) {
    return null;
  }

  try {
    const completionInfo = await lspClient.getCompletion(model.getValue(), {
      line: position.lineNumber - 1,
      character: position.column - 1,
    });

    if (!completionInfo) {
      return null;
    }

    const completionItems = Array.isArray(completionInfo) ? completionInfo : completionInfo.items;

    if (!completionItems) {
      return null;
    }

    return {
      suggestions: completionItems.map((item) => {
        return convertCompletionItem(item, model);
      }),
      incomplete: Array.isArray(completionInfo) ? false : completionInfo.isIncomplete,
      dispose: () => {},
    };
  } catch {
    return null;
  }
}

async function handleResolveCompletionRequest(item: monaco.languages.CompletionItem): Promise<monaco.languages.CompletionItem | null> {
  const extendedItem = item as unknown as ExtendedCompletionItem;
  const model = extendedItem.model;
  const original = extendedItem.__original;
  if (!model || !original) {
    return null;
  }

  const lspClient = getLspClientForModel(model);
  if (!lspClient) {
    return null;
  }

  try {
    const result = await lspClient.resolveCompletion(original);
    if (!result) {
      return null;
    }
    return convertCompletionItem(result);
  } catch {
    return null;
  }
}

// Extended interface for CompletionItem with custom properties we attach
interface ExtendedCompletionItem extends monaco.languages.CompletionItem {
  __original?: CompletionItem;
  model?: monaco.editor.ITextModel;
}

function convertCompletionItem(item: CompletionItem, model?: monaco.editor.ITextModel): monaco.languages.CompletionItem {
  const converted: Partial<monaco.languages.CompletionItem> & {
    label: string;
    kind: monaco.languages.CompletionItemKind;
    insertText: string;
  } = {
    label: item.label,
    kind: item.kind !== undefined ? convertCompletionItemKind(item.kind) : monaco.languages.CompletionItemKind.Text,
    tags: item.tags,
    detail: item.detail,
    documentation: item.documentation,
    sortText: item.sortText,
    filterText: item.filterText,
    preselect: item.preselect,
    insertText: item.label,
  };

  if (item.textEdit) {
    converted.insertText = item.textEdit.newText;
    if (InsertReplaceEdit.is(item.textEdit)) {
      converted.range = {
        insert: convertRange(item.textEdit.insert),
        replace: convertRange(item.textEdit.replace),
      };
    } else {
      converted.range = convertRange(item.textEdit.range);
    }
  }

  if (item.additionalTextEdits) {
    converted.additionalTextEdits = item.additionalTextEdits.map((edit) => {
      return {
        range: convertRange(edit.range),
        text: edit.newText,
      };
    });
  }

  // Stash a few additional pieces of information.
  const extendedConverted = converted as unknown as ExtendedCompletionItem;
  extendedConverted.__original = item;
  if (model) {
    extendedConverted.model = model;
  }

  return extendedConverted as monaco.languages.CompletionItem;
}

function convertCompletionItemKind(itemKind: CompletionItemKind): monaco.languages.CompletionItemKind {
  switch (itemKind) {
    case CompletionItemKind.Constant:
      return monaco.languages.CompletionItemKind.Constant;

    case CompletionItemKind.Variable:
      return monaco.languages.CompletionItemKind.Variable;

    case CompletionItemKind.Function:
      return monaco.languages.CompletionItemKind.Function;

    case CompletionItemKind.Field:
      return monaco.languages.CompletionItemKind.Field;

    case CompletionItemKind.Keyword:
      return monaco.languages.CompletionItemKind.Keyword;

    default:
      return monaco.languages.CompletionItemKind.Reference;
  }
}

// Register an instantiated text model (which backs a monaco editor
// instance and its associated LSP client. This is a bit of a hack,
// but it's required to support the various providers (e.g. hover).
function registerModel(model: monaco.editor.ITextModel, lspClient: LspClient) {
  const registeredModel = registeredModels.find((m) => m.model === model);
  if (registeredModel) {
    registeredModel.lspClient = lspClient;
    return;
  }

  registeredModels.push({ model, lspClient });
}

function getLspClientForModel(model: monaco.editor.ITextModel): LspClient | undefined {
  return registeredModels.find((m) => m.model === model)?.lspClient;
}
